import { Injectable, inject } from '@angular/core';
import { WatchQueryFetchPolicy, ObservableQuery } from '@apollo/client';
import { ToastService } from '@ih/app/client/shared/services';
import { Project } from '@ih/app/shared/apis/interfaces';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { produce } from 'immer';
import { catchError, take, tap } from 'rxjs';
import {
  FindAllProjects,
  ResetGroupProjectsList,
  SetProjects,
} from '../actions';
import { FindAllProjectsError, FindAllProjectsSuccess } from '../effects';

export interface GroupProjectsListStateModel {
  projects: Project[];
}

@State<GroupProjectsListStateModel>({
  name: 'groupProjectsList',
  defaults: {
    projects: [],
  },
})
@Injectable()
export class GroupProjectsListState {
  private readonly apollo = inject(Apollo);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);

  @Selector()
  public static getState(state: GroupProjectsListStateModel) {
    return state;
  }

  @Selector()
  public static projects(state: GroupProjectsListStateModel): Project[] {
    return state.projects;
  }

  @Action(FindAllProjects)
  public findAllProjects(ctx: StateContext<GroupProjectsListStateModel>) {
    const groupId = this.store.selectSnapshot<string>(
      (state) => state.group.group.id,
    );

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindAllProjects($groupId: String!) {
        findAllProjects(
          request: { where: { groupId: $groupId, deleted: false } }
        ) {
          id
          name
          description
          slug
          photoURL
        }
      }
    `;

    const postsQuery = this.apollo.watchQuery<{ findAllProjects: Project[] }>({
      query,
      variables: {
        groupId,
      },
      fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
    });
    postsQuery.refetch();
    return postsQuery.valueChanges.pipe(
      take(1),
      tap((result: ObservableQuery.Result<{ findAllProjects: Project[] }>) => {
        if (result.data && networkStatus) {
          this.apollo.client.cache.writeQuery({
            query,
            data: result.data,
          });
        }
        ctx.dispatch(new FindAllProjectsSuccess(result));
      }),
      catchError((error) => ctx.dispatch(new FindAllProjectsError(error))),
    );
  }

  @Action(FindAllProjectsSuccess)
  public findAllProjectsSuccess(
    ctx: StateContext<GroupProjectsListStateModel>,
    { result }: FindAllProjectsSuccess,
  ) {
    const projects = (result?.data?.findAllProjects || []) as Project[];
    ctx.dispatch(new SetProjects(projects));
  }

  @Action(FindAllProjectsError)
  public findAllProjectsError(
    ctx: StateContext<GroupProjectsListStateModel>,
    { error }: FindAllProjectsError,
  ) {
    this.toastService.showError('Failed to fetch projects');
    console.error('Failed to fetch projects: ', error.message);
  }

  @Action(SetProjects)
  public setProjects(
    ctx: StateContext<GroupProjectsListStateModel>,
    { projects }: SetProjects,
  ) {
    ctx.setState(
      produce((draft) => {
        draft.projects = projects;
      }),
    );
  }

  @Action(ResetGroupProjectsList)
  public ResetGroupProjectsList(
    ctx: StateContext<GroupProjectsListStateModel>,
  ) {
    ctx.setState(
      produce((draft) => {
        draft.projects = [];
      }),
    );
  }
}
